import { useCallback, useEffect, useState } from 'react';
import tprmService from '../../../services/compliance/tprm.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import {
  IncidentSeverity,
  IncidentStatus,
  IncidentType,
  VendorIncident,
  VendorIncidentCreate,
} from '../../../types/compliance';
import Notification, { NotificationContainer } from '../../../components/Notification';


const SEVERITY_BADGE: Record<IncidentSeverity, string> = {
  low: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  medium: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  critical: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const STATUS_BADGE: Record<IncidentStatus, string> = {
  open: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  investigating: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  contained: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  resolved: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  closed: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const TYPE_LABEL: Record<IncidentType, string> = {
  service_outage: 'Service outage',
  security_breach: 'Security breach',
  data_breach: 'Data breach',
  regulatory_finding: 'Regulatory finding',
  performance_exception: 'Performance exception',
  other: 'Other',
};


interface Props {
  vendorId: string;
}


function nowLocalDatetime(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  // datetime-local format: YYYY-MM-DDTHH:MM
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}


export default function IncidentsPanel({ vendorId }: Props) {
  const { isManager: canEdit } = useComplianceAccess();

  const [incidents, setIncidents] = useState<VendorIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<{ title: string; message: string } | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [incidentType, setIncidentType] = useState<IncidentType>('service_outage');
  const [severity, setSeverity] = useState<IncidentSeverity>('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [impact, setImpact] = useState('');
  const [occurredAt, setOccurredAt] = useState<string>(nowLocalDatetime());
  const [discoveredAt, setDiscoveredAt] = useState<string>('');
  const [regNotified, setRegNotified] = useState(false);
  const [custNotified, setCustNotified] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await tprmService.listIncidents(vendorId);
      setIncidents(rows);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setErr({ title: 'Failed to load incidents', message: msg });
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setIncidentType('service_outage');
    setSeverity('medium');
    setTitle('');
    setDescription('');
    setImpact('');
    setOccurredAt(nowLocalDatetime());
    setDiscoveredAt('');
    setRegNotified(false);
    setCustNotified(false);
  };

  const submit = useCallback(async () => {
    if (!title.trim()) {
      setErr({ title: 'Title required', message: 'Short summary of the incident' });
      return;
    }
    if (!description.trim()) {
      setErr({ title: 'Description required', message: 'Describe what happened' });
      return;
    }
    if (!occurredAt) {
      setErr({ title: 'Occurred-at required', message: 'When did the incident start' });
      return;
    }
    const body: VendorIncidentCreate = {
      incident_type: incidentType,
      severity,
      title: title.trim(),
      description: description.trim(),
      impact_description: impact.trim() || undefined,
      occurred_at: new Date(occurredAt).toISOString(),
      discovered_at: discoveredAt ? new Date(discoveredAt).toISOString() : undefined,
      regulatory_notified: regNotified,
      customer_notified: custNotified,
    };
    setSaving(true);
    try {
      const created = await tprmService.createIncident(vendorId, body);
      setIncidents((prev) => [created, ...prev]);
      setFormOpen(false);
      resetForm();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      setErr({ title: 'Failed to log incident', message: msg });
    } finally {
      setSaving(false);
    }
  }, [
    vendorId, incidentType, severity, title, description, impact,
    occurredAt, discoveredAt, regNotified, custNotified,
  ]);

  const transitionStatus = useCallback(
    async (inc: VendorIncident, status: IncidentStatus) => {
      setBusyId(inc.id);
      try {
        const updated = await tprmService.updateIncident(vendorId, inc.id, { status });
        setIncidents((prev) => prev.map((i) => (i.id === inc.id ? updated : i)));
      } catch (error: any) {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
        setErr({ title: 'Failed to update status', message: msg });
      } finally {
        setBusyId(null);
      }
    },
    [vendorId],
  );

  const openCount = incidents.filter((i) => i.is_open).length;

  return (
    <div className="space-y-4">
      <NotificationContainer>
        {err && (
          <Notification
            type="error"
            title={err.title}
            message={err.message}
            onClose={() => setErr(null)}
          />
        )}
      </NotificationContainer>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {loading
            ? 'Loading…'
            : `${incidents.length} logged · ${openCount} open.`}
        </div>
        {canEdit && !formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            + Log Incident
          </button>
        )}
      </div>

      {formOpen && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1 text-label">Type</span>
              <select
                value={incidentType}
                onChange={(e) => setIncidentType(e.target.value as IncidentType)}
                className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
              >
                {(Object.keys(TYPE_LABEL) as IncidentType[]).map((k) => (
                  <option key={k} value={k}>{TYPE_LABEL[k]}</option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-label">Severity</span>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
                className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </label>
          </div>

          <label className="text-sm block">
            <span className="block mb-1 text-label">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary"
              className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            />
          </label>

          <label className="text-sm block">
            <span className="block mb-1 text-label">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            />
          </label>

          <label className="text-sm block">
            <span className="block mb-1 text-label">Impact (optional)</span>
            <textarea
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
              rows={2}
              className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1 text-label">Occurred at</span>
              <input
                type="datetime-local"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-label">Discovered at (optional)</span>
              <input
                type="datetime-local"
                value={discoveredAt}
                onChange={(e) => setDiscoveredAt(e.target.value)}
                className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
              />
            </label>
          </div>

          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={regNotified}
                onChange={(e) => setRegNotified(e.target.checked)}
              />
              Regulator notified
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={custNotified}
                onChange={(e) => setCustNotified(e.target.checked)}
              />
              Customers notified
            </label>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setFormOpen(false); resetForm(); }}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Log Incident'}
            </button>
          </div>
        </div>
      )}

      {!loading && incidents.length === 0 && !formOpen && (
        <div className="text-sm text-muted">
          No incidents logged.
        </div>
      )}

      <ul className="space-y-2">
        {incidents.map((i) => (
          <li
            key={i.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-panel"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded uppercase ${SEVERITY_BADGE[i.severity]}`}>
                    {i.severity}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded uppercase ${STATUS_BADGE[i.status]}`}>
                    {i.status.replace('_', ' ')}
                  </span>
                  <span className="text-xs text-muted">
                    {TYPE_LABEL[i.incident_type]}
                  </span>
                </div>
                <div className="mt-1 font-medium text-body">
                  {i.title}
                </div>
                <div className="mt-1 text-xs text-muted">
                  Occurred {new Date(i.occurred_at).toLocaleString()}
                  {i.resolved_at && ` · Resolved ${new Date(i.resolved_at).toLocaleString()}`}
                </div>
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                  {i.description}
                </p>
                {i.impact_description && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                    <span className="font-semibold">Impact:</span> {i.impact_description}
                  </p>
                )}
                {(i.regulatory_notified || i.customer_notified) && (
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 flex gap-3">
                    {i.regulatory_notified && <span>✓ Regulator notified</span>}
                    {i.customer_notified && <span>✓ Customers notified</span>}
                  </div>
                )}
              </div>
              {canEdit && i.is_open && (
                <div className="flex flex-col gap-1">
                  {i.status === 'open' && (
                    <button
                      type="button"
                      onClick={() => transitionStatus(i, 'investigating')}
                      disabled={busyId === i.id}
                      className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-highlight disabled:opacity-50"
                    >
                      Investigate
                    </button>
                  )}
                  {i.status === 'investigating' && (
                    <button
                      type="button"
                      onClick={() => transitionStatus(i, 'contained')}
                      disabled={busyId === i.id}
                      className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-highlight disabled:opacity-50"
                    >
                      Contain
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => transitionStatus(i, 'resolved')}
                    disabled={busyId === i.id}
                    className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Resolve
                  </button>
                </div>
              )}
              {canEdit && i.status === 'resolved' && (
                <button
                  type="button"
                  onClick={() => transitionStatus(i, 'closed')}
                  disabled={busyId === i.id}
                  className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-highlight disabled:opacity-50"
                >
                  Close
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
